import { useRef, useEffect } from 'react';
import * as THREE from 'three';

export default function TruncatedIcosahedron({ visible }) {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Scene setup
    const scene = new THREE.Scene();

    // Camera
    const camera = new THREE.PerspectiveCamera(
      50,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.z = 5;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true 
    });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Create Truncated Icosahedron (soccer ball shape - 32 faces: 20 hexagons + 12 pentagons)
    // Golden ratio
    const phi = (1 + Math.sqrt(5)) / 2;
    const scale = 1.2;

    
    // Generate all 60 vertices using the known coordinates
    const coords = [
      // (0, ±1, ±3φ)
      [0, 1, 3*phi], [0, 1, -3*phi], [0, -1, 3*phi], [0, -1, -3*phi],
      // (±1, ±3φ, 0)
      [1, 3*phi, 0], [-1, 3*phi, 0], [1, -3*phi, 0], [-1, -3*phi, 0],
      // (±3φ, 0, ±1)
      [3*phi, 0, 1], [3*phi, 0, -1], [-3*phi, 0, 1], [-3*phi, 0, -1],
      // (±2, ±(1+2φ), ±φ)
      [2, 1+2*phi, phi], [2, 1+2*phi, -phi], [-2, 1+2*phi, phi], [-2, 1+2*phi, -phi],
      [2, -(1+2*phi), phi], [2, -(1+2*phi), -phi], [-2, -(1+2*phi), phi], [-2, -(1+2*phi), -phi],
      // (±(1+2φ), ±φ, ±2)
      [1+2*phi, phi, 2], [1+2*phi, phi, -2], [1+2*phi, -phi, 2], [1+2*phi, -phi, -2],
      [-(1+2*phi), phi, 2], [-(1+2*phi), phi, -2], [-(1+2*phi), -phi, 2], [-(1+2*phi), -phi, -2],
      // (±φ, ±2, ±(1+2φ))
      [phi, 2, 1+2*phi], [phi, 2, -(1+2*phi)], [phi, -2, 1+2*phi], [phi, -2, -(1+2*phi)],
      [-phi, 2, 1+2*phi], [-phi, 2, -(1+2*phi)], [-phi, -2, 1+2*phi], [-phi, -2, -(1+2*phi)],
      // (±1, ±(2+φ), ±2φ)
      [1, 2+phi, 2*phi], [1, 2+phi, -2*phi], [-1, 2+phi, 2*phi], [-1, 2+phi, -2*phi],
      [1, -(2+phi), 2*phi], [1, -(2+phi), -2*phi], [-1, -(2+phi), 2*phi], [-1, -(2+phi), -2*phi],
      // (±(2+φ), ±2φ, ±1)
      [2+phi, 2*phi, 1], [2+phi, 2*phi, -1], [2+phi, -2*phi, 1], [2+phi, -2*phi, -1],
      [-(2+phi), 2*phi, 1], [-(2+phi), 2*phi, -1], [-(2+phi), -2*phi, 1], [-(2+phi), -2*phi, -1],
      // (±2φ, ±1, ±(2+φ))
      [2*phi, 1, 2+phi], [2*phi, 1, -(2+phi)], [2*phi, -1, 2+phi], [2*phi, -1, -(2+phi)],
      [-2*phi, 1, 2+phi], [-2*phi, 1, -(2+phi)], [-2*phi, -1, 2+phi], [-2*phi, -1, -(2+phi)],
    ];

    // Normalize and scale vertices
    const normalizedVertices = coords.map(([x, y, z]) => {
      const len = Math.sqrt(x*x + y*y + z*z);
      return new THREE.Vector3(x/len * scale * 2, y/len * scale * 2, z/len * scale * 2);
    });

    // Create edges geometry
    const edgesGeometry = new THREE.BufferGeometry();
    const edgePositions = [];
    
    // Find edges (vertices that are close enough to be connected)
    const edgeLength = 0.4 * scale * 2; // Approximate edge length after normalization
    const tolerance = 0.15;
    
    for (let i = 0; i < normalizedVertices.length; i++) {
      for (let j = i + 1; j < normalizedVertices.length; j++) {
        const dist = normalizedVertices[i].distanceTo(normalizedVertices[j]);
        if (Math.abs(dist - edgeLength) < tolerance) {
          edgePositions.push(
            normalizedVertices[i].x, normalizedVertices[i].y, normalizedVertices[i].z,
            normalizedVertices[j].x, normalizedVertices[j].y, normalizedVertices[j].z
          );
        }
      }
    }

    edgesGeometry.setAttribute('position', new THREE.Float32BufferAttribute(edgePositions, 3));

    // Steel/chrome material for edges - bright luminous effect
    const edgesMaterial = new THREE.LineBasicMaterial({ 
      color: 0x87ceeb, // Steel blue
      linewidth: 2,
      transparent: true,
      opacity: 0.95,
    });
    
    const wireframe = new THREE.LineSegments(edgesGeometry, edgesMaterial);

    // Add glowing vertices (60 points)
    const pointsGeometry = new THREE.BufferGeometry();
    const pointPositions = [];
    normalizedVertices.forEach(v => {
      pointPositions.push(v.x, v.y, v.z);
    });
    pointsGeometry.setAttribute('position', new THREE.Float32BufferAttribute(pointPositions, 3));
    
    const pointsMaterial = new THREE.PointsMaterial({
      color: 0x60a5fa, // Blue glow
      size: 0.08,
      transparent: true,
      opacity: 0.9,
      sizeAttenuation: true,
    });
    
    const points = new THREE.Points(pointsGeometry, pointsMaterial);

    // Create group
    const group = new THREE.Group();
    group.add(wireframe);
    group.add(points);
    scene.add(group);

    // Add ambient glow
    const glowLight = new THREE.PointLight(0x60a5fa, 1.5, 10);
    glowLight.position.set(0, 0, 3);
    scene.add(glowLight);

    const steelLight = new THREE.PointLight(0xc0c0c0, 1, 10);
    steelLight.position.set(2, 2, 2);
    scene.add(steelLight);

    // Animation loop
    let time = 0;
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      time += 0.01;
      
      group.rotation.x += 0.002;
      group.rotation.y += 0.003;
      
      // Pulsing glow effect
      edgesMaterial.opacity = 0.7 + Math.sin(time * 2) * 0.25;
      pointsMaterial.opacity = 0.6 + Math.sin(time * 2 + 1) * 0.3;
      
      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      if (rendererRef.current && container) {
        container.removeChild(rendererRef.current.domElement);
      }
      edgesGeometry.dispose();
      edgesMaterial.dispose();
      pointsGeometry.dispose();
      pointsMaterial.dispose();
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      className="w-full h-full transition-opacity duration-1000"
      style={{ 
        opacity: visible ? 1 : 0,
        minHeight: '400px'
      }}
    />
  );
}